import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { CreditCard, Clock, AlertTriangle, Loader2, Calendar, XCircle, Settings2 } from 'lucide-react';

/**
 * Current Plan card — top of the BillingTab.
 * Shows trial/active/expired state, billing breakdown, and Manage Billing /
 * Cancel actions for active subscribers.
 */
export default function CurrentPlanCard({
  sub, trial, stripeDetails, isTrialing, isActive, isExpired, isCanceling,
  planLabel, portalLoading, cancelLoading, handlePortal, handleCancel,
}) {
  return (
    <Card data-testid="current-plan-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-teal-500" />
            <CardTitle className="text-lg">Current Plan</CardTitle>
          </div>
          <span
            className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
              isActive ? 'bg-green-100 text-green-700' :
              isTrialing ? 'bg-blue-100 text-blue-700' :
              'bg-red-100 text-red-700'
            }`}
            data-testid="plan-status-badge"
          >
            {isActive ? 'Active' : isTrialing ? 'Free Trial' : 'Expired'}
          </span>
        </div>
        <CardDescription>
          {isTrialing && `${trial.days_remaining} days remaining in your ${planLabel} free trial`}
          {isActive && !isCanceling && `${planLabel} plan — $${sub.billing?.total_monthly?.toFixed(2)}/mo (recurring monthly)`}
          {isActive && isCanceling && `${planLabel} plan — cancels at end of billing period`}
          {isExpired && 'Your free trial has ended. Subscribe to continue using all features.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isTrialing && (
          <div className="bg-blue-50 rounded-lg p-4 space-y-2" data-testid="trial-info">
            <div className="flex items-center gap-2 text-sm font-medium text-blue-800">
              <Clock className="w-4 h-4" />
              Trial ends {new Date(trial.trial_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
            <p className="text-xs text-blue-600">
              All features included. Subscribe before your trial ends to keep access.
            </p>
          </div>
        )}
        {isExpired && (
          <div className="bg-red-50 rounded-lg p-4 flex items-start gap-2" data-testid="expired-info">
            <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700">
              Your trial has expired. Subscribe now to restore full access to your data and features.
            </p>
          </div>
        )}
        {isActive && sub.billing && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="active-billing-info">
              <div className="bg-gray-50 rounded p-3">
                <p className="text-gray-500 text-xs">Base</p>
                <p className="font-semibold">${sub.billing.base?.toFixed(2)}</p>
              </div>
              <div className="bg-gray-50 rounded p-3">
                <p className="text-gray-500 text-xs">Add-ons</p>
                <p className="font-semibold">${(sub.billing.total_monthly - sub.billing.base).toFixed(2)}</p>
              </div>
              <div className="bg-gray-50 rounded p-3">
                <p className="text-gray-500 text-xs">Monthly Total</p>
                <p className="font-semibold text-teal-600">${sub.billing.total_monthly?.toFixed(2)}</p>
              </div>
              <div className="bg-gray-50 rounded p-3">
                <p className="text-gray-500 text-xs">Billing</p>
                <p className="font-semibold text-green-600">Recurring</p>
              </div>
            </div>
            {stripeDetails.current_period_end && (
              <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg px-4 py-2.5" data-testid="billing-period-info">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span>
                  Current period: {new Date(stripeDetails.current_period_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — {new Date(stripeDetails.current_period_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
            )}
            {isCanceling && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-start gap-2 text-sm text-amber-700" data-testid="cancel-notice">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p>Your subscription will cancel at the end of the current billing period. You will retain access until then.</p>
              </div>
            )}
            {!isCanceling && (
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-gray-600 hover:text-gray-800 text-xs"
                  onClick={handlePortal}
                  disabled={portalLoading}
                  data-testid="manage-billing-btn"
                >
                  {portalLoading
                    ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Opening...</>
                    : <><Settings2 className="w-3 h-3 mr-1.5" /> Manage Billing</>}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-700 hover:bg-red-50 text-xs"
                  onClick={handleCancel}
                  disabled={cancelLoading}
                  data-testid="cancel-subscription-btn"
                >
                  {cancelLoading
                    ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Canceling...</>
                    : <><XCircle className="w-3 h-3 mr-1" /> Cancel Subscription</>}
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
